import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Switch } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getSessionUser } from "../../../utils/auth/sessionUser";

import { createInvoice, updateInvoice } from "../../../api/finance/FinanceApi";
import { financeKeys } from "../../../api/finance/financeQueryKeys";
import { ClinicSettings, Invoice, LocalPaymentMethod } from "../../../types/finance/FinanceTypes";
import { PriceEntryMode, TaxCode } from "../../../utils/finance/gst";
import { financeErrorMessage } from "../../../utils/finance/financeError";
import { isDraft } from "../../../utils/finance/invoiceDisplay";
import { can } from "../../../utils/userPrefs/validatePermissions";
import { useNotifications } from "../../../context/NotificationContext";
import PatientSelect from "./PatientSelect";
import InvoiceLineEditor from "./InvoiceLineEditor";
import {
	InvoiceFormValues,
	buildCreateInvoiceBody,
	buildUpdateInvoiceBody,
	createInitialValues,
	editInitialValues,
	resolvePractitionerId,
} from "./invoiceRequest";
import type { UserUuid } from "../../../types/identity";

/**
 * Create a new invoice, or edit an existing DRAFT.
 *
 * ── Shared on purpose ────────────────────────────────────────────────────────────────────────
 *
 * `InvoicesPage` mounts it today; the patient record's Billing tab mounts the same component in
 * I9 with `fixedUserId` set. Everything patient-specific arrives as a prop and nothing is read
 * from the route, so the second mount site needs no fork.
 *
 * ── One create endpoint ──────────────────────────────────────────────────────────────────────
 *
 * `POST /invoices` covers both collection paths — `collectViaStripe` is a flag on the body, not a
 * different endpoint. ⚠️ **Nothing is created in Stripe by this request.** The draft is local and
 * freely editable; the Stripe mirror is built once, at ISSUE, from the frozen lines. The old
 * `POST /invoices/stripe` is deprecated, returns `stripeInvoice: null`, and is not called here.
 *
 * ── One `practitionerId`, resolved once (S12 / decision D7) ──────────────────────────────────
 *
 * The appointment-type picker quotes `custom_price ?? default_price` FOR A NAMED PRACTITIONER, and
 * the server re-reads the same catalogue to snapshot the price it stores. If the picker and the
 * request disagree about who the practitioner is, the snapshot is not the figure that was quoted
 * and `priceOverridden` reads true on a line nobody discounted. So it is resolved here, in one
 * place, and handed to `InvoiceLineEditor` AND to both request builders.
 */

export interface CreateInvoiceModalProps {
	open: boolean;
	onClose: () => void;
	/** Pass a DRAFT to edit it. Anything else is a 409 server-side, so the modal refuses it. */
	invoice?: Invoice | null;
	/** Clinic defaults — entry mode, tax code, payment terms. `null` while loading or unset. */
	settings?: ClinicSettings | null;
	/** Pre-selects and locks the patient (the Billing-tab mount). */
	fixedUserId?: string;
	/**
	 * CU-86d45b54n — how a fixed recipient who is not a patient is shown: its own label and a display
	 * name, so no patient search runs for the label. `fixedUserId` still carries the id on the wire.
	 */
	fixedRecipient?: { label: string; name: string };
	/**
	 * Whose rates price the appointment-type lines. Defaults to the signed-in user when they are
	 * clinical staff — see `loggedInPractitionerId`.
	 */
	practitionerId?: UserUuid;
	onSaved?: (invoice: Invoice) => void;
}

// Billing staff may raise invoices but hold no practitioner rates, so only a consult host sends their own id.
function loggedInPractitionerId(): UserUuid | undefined {
	return can("talk.consult.host") ? resolvePractitionerId(getSessionUser().id) : undefined;
}

// CU-86d45b54n: Form.Item injects the stored id as `value`; the field shows the recipient's name instead.
const FixedRecipientField: React.FC<{ name: string; id?: string; "data-testid"?: string }> = ({ name, id, ...rest }) => (
	<Input id={id} value={name} disabled data-testid={rest["data-testid"]} />
);

const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({
	open,
	onClose,
	invoice,
	settings,
	fixedUserId,
	fixedRecipient,
	practitionerId,
	onSaved,
}) => {
	const [form] = Form.useForm<InvoiceFormValues>();
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useNotifications();

	const isEdit = !!invoice;
	// ONE value, read by the picker and by both request builders. See the docblock.
	const resolvedPractitionerId = useMemo(
		() => resolvePractitionerId(practitionerId) ?? loggedInPractitionerId(),
		[practitionerId],
	);
	const defaultTaxCode = (settings?.defaultTaxCode as TaxCode) ?? TaxCode.GST_FREE_HEALTH;
	const defaultMode = (settings?.defaultPriceEntryMode as PriceEntryMode) ?? PriceEntryMode.INCLUSIVE;

	// Watched rather than read on submit, because the price input's "(inc. GST)" / "(ex. GST)"
	// label and every line preview depend on it live.
	const mode = Form.useWatch("priceEntryMode", form) ?? defaultMode;
	const collectViaStripe = Form.useWatch("collectViaStripe", form) ?? false;
	// PAYLINK/PAYID: the per-invoice pay methods (a SET), watched so the "not configured" hints react
	// live to what the operator ticks.
	const localPaymentMethods =
		Form.useWatch("localPaymentMethods", form) ?? [LocalPaymentMethod.BANK_TRANSFER];
	// Non-blocking hints: a method is chosen but the clinic has not configured it in Finance settings.
	const showNoPaymentLinkHint =
		!collectViaStripe &&
		localPaymentMethods.includes(LocalPaymentMethod.PAYMENT_LINK) &&
		!settings?.invoicePaymentLink;
	const showNoPayIdHint =
		!collectViaStripe &&
		localPaymentMethods.includes(LocalPaymentMethod.PAYID) &&
		!(settings?.payId && settings?.payIdType);

	const initialValues = useMemo<InvoiceFormValues>(
		() =>
			invoice
				? editInitialValues(invoice, { defaultMode, defaultTaxCode })
				: createInitialValues({
						fixedUserId,
						defaultMode,
						defaultTaxCode,
						defaultPaymentTermsDays: settings?.defaultPaymentTermsDays,
					}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[invoice, fixedUserId, settings],
	);

	// `destroyOnClose` throws the fields away on close, so the reset has to happen on OPEN —
	// otherwise re-opening for a different invoice renders the previous one's lines.
	//
	// ⚠️ The dep array is `[open]` ALONE, and `initialValues` is read through a ref. With
	// `initialValues` in the deps, a clinic-settings query that resolves a moment after the modal
	// opened would recompute it and reset the form UNDER the operator, wiping lines they had
	// already typed. The cost is that a very late `settings` arrival does not backfill the
	// defaults — which is harmless, because every one of those fields is optional on the wire and
	// the server applies the same clinic defaults for anything left undefined.
	const initialValuesRef = React.useRef(initialValues);
	initialValuesRef.current = initialValues;

	useEffect(() => {
		if (open) {
			form.setFieldsValue(initialValuesRef.current);
		}
	}, [open, form]);

	const [submitError, setSubmitError] = useState<string | null>(null);

	const mutation = useMutation({
		mutationFn: async (values: InvoiceFormValues): Promise<Invoice> =>
			isEdit && invoice
				? updateInvoice(invoice.id, buildUpdateInvoiceBody(values, resolvedPractitionerId))
				: createInvoice(buildCreateInvoiceBody(values, resolvedPractitionerId)),
		onSuccess: (saved) => {
			queryClient.invalidateQueries({ queryKey: financeKeys.invoices() });
			if (saved?.id) {
				queryClient.invalidateQueries({ queryKey: financeKeys.invoice(saved.id) });
			}
			showSuccess(isEdit ? "Draft updated" : "Draft invoice created");
			setSubmitError(null);
			onSaved?.(saved);
			onClose();
		},
		onError: (error) => {
			// Surfaced in the modal AND as a toast: the modal stays open with the operator's
			// lines intact, and the server's own wording (a 409 on a non-draft, a validation
			// list) is what they read rather than a generic failure.
			const message = financeErrorMessage(
				error,
				isEdit ? "Failed to update invoice" : "Failed to create invoice",
			);
			setSubmitError(message);
			showError(message);
		},
	});

	const nonDraftEdit = isEdit && !!invoice && !isDraft(invoice);
	const showFixedRecipient = !!fixedRecipient && !!fixedUserId;
	const recipientLabel = showFixedRecipient ? fixedRecipient.label : "Patient";

	return (
		<Modal
			title={isEdit ? "Edit draft invoice" : "Create invoice"}
			open={open}
			onCancel={() => {
				setSubmitError(null);
				onClose();
			}}
			width={760}
			destroyOnClose
			footer={[
				<Button key="cancel" onClick={onClose}>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					loading={mutation.isPending}
					disabled={nonDraftEdit}
					onClick={() => form.submit()}
					data-testid="invoice-modal-submit"
				>
					{isEdit ? "Save draft" : "Create draft"}
				</Button>,
			]}
		>
			{nonDraftEdit && (
				<Alert
					type="warning"
					showIcon
					style={{ marginBottom: 12 }}
					message="This invoice has been issued and can no longer be edited."
					description="An issued invoice is a legal document. Cancel it and raise a new one if it is wrong."
				/>
			)}

			{submitError && (
				<Alert
					type="error"
					showIcon
					closable
					style={{ marginBottom: 12 }}
					message={submitError}
					onClose={() => setSubmitError(null)}
				/>
			)}

			<Form
				form={form}
				layout="vertical"
				initialValues={initialValues}
				onFinish={(values) => mutation.mutate(values)}
			>
				<Row gutter={[16, 0]}>
					<Col xs={24} md={12}>
						<Form.Item
							label={recipientLabel}
							name="userId"
							rules={[{ required: true, message: `Select the ${recipientLabel.toLowerCase()} this invoice is for` }]}
						>
							{showFixedRecipient ? (
								<FixedRecipientField name={fixedRecipient.name} data-testid="invoice-modal-recipient" />
							) : (
								<PatientSelect
									disabled={!!fixedUserId || isEdit}
									data-testid="invoice-modal-patient"
								/>
							)}
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item label="Price entry" name="priceEntryMode">
							<Select
								options={[
									{ value: PriceEntryMode.INCLUSIVE, label: "Prices include GST" },
									{ value: PriceEntryMode.EXCLUSIVE, label: "Prices exclude GST" },
								]}
								data-testid="invoice-modal-mode"
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={[16, 0]}>
					<Col xs={24} md={8}>
						<Form.Item
							label="Collect via Stripe"
							name="collectViaStripe"
							valuePropName="checked"
							extra="Nothing is created in Stripe until the invoice is issued."
						>
							<Switch data-testid="invoice-modal-stripe" />
						</Form.Item>
					</Col>
					{/*
					  PAYLINK/PAYID: a MULTI-select of pay methods for a NON-Stripe invoice — any
					  subset, at least one, every selected+configured one rendered on the invoice.
					  Hidden entirely when collecting via Stripe — that invoice is paid on its hosted
					  page regardless.
					*/}
					{!collectViaStripe && (
						<Col xs={24} md={8}>
							<Form.Item
								label="Payment methods"
								name="localPaymentMethods"
								rules={[
									{
										required: true,
										type: "array",
										min: 1,
										message: "Choose at least one payment method",
									},
								]}
								extra={
									showNoPaymentLinkHint || showNoPayIdHint ? (
										<span
											data-testid="invoice-modal-method-hints"
											style={{ color: "var(--mg-warning, #d46b08)" }}
										>
											{showNoPaymentLinkHint && (
												<span data-testid="invoice-modal-no-paylink-hint">
													No payment link configured — set one in Finance settings.{" "}
												</span>
											)}
											{showNoPayIdHint && (
												<span data-testid="invoice-modal-no-payid-hint">
													No PayID configured — set one in Finance settings.
												</span>
											)}
										</span>
									) : (
										"Shown on the invoice so the patient can pay. Choose any combination."
									)
								}
							>
								<Checkbox.Group
									options={[
										{ value: LocalPaymentMethod.BANK_TRANSFER, label: "Bank transfer" },
										{ value: LocalPaymentMethod.PAYMENT_LINK, label: "Payment link" },
										{ value: LocalPaymentMethod.PAYID, label: "PayID" },
									]}
									data-testid="invoice-modal-payment-methods"
								/>
							</Form.Item>
						</Col>
					)}
					{collectViaStripe && (
						<Col xs={24} md={8}>
							<Form.Item
								label="Stripe customer ID"
								name="stripeCustomerId"
								extra="Required at issue, not now."
								
							>
								<Input placeholder="cus_…" disabled />
							</Form.Item>
						</Col>
					)}
					{/*
					  ⚠️ Payment terms on CREATE, an explicit due date on EDIT — never both, and the
					  swap is the fix for an edit silently moving the due date.

					  A saved invoice has no `paymentTermsDays`: the server consumed it once, at
					  create, to derive `dueDate`, and stored only the date. So on edit there is
					  nothing to prefill the terms field from, and a body carrying a term but no
					  date makes the server recompute the due date from it. Showing the real due
					  date instead means an edit that changes nothing sends the date back unchanged.
					*/}
					{!isEdit && (
						<Col xs={24} md={8}>
							<Form.Item
								label="Payment terms (days)"
								name="paymentTermsDays"
								extra={
									settings?.defaultPaymentTermsDays !== undefined
										? `Clinic default: ${settings.defaultPaymentTermsDays} days`
										: "Leave blank to use the clinic default"
								}
							>
								<InputNumber min={0} max={365} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
					)}
					<Col xs={24} md={8}>
						<Form.Item label="Issue date" name="issueDate">
							<DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
						</Form.Item>
					</Col>
					{isEdit && (
						<Col xs={24} md={8}>
							<Form.Item label="Due date" name="dueDate">
								<DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
							</Form.Item>
						</Col>
					)}
				</Row>

				<Form.Item label="Lines" name="lines">
					<InvoiceLineEditor
						mode={mode}
						defaultTaxCode={defaultTaxCode}
						practitionerId={resolvedPractitionerId}
					/>
				</Form.Item>

				<Form.Item label="Notes" name="notes">
					<Input.TextArea rows={2} placeholder="Internal notes…" />
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default CreateInvoiceModal;
